import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Zap, Check, X, Copy, Trash2, Users, FileText, HelpCircle, Eye, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { PageHeader } from "@/pages/admin/AdminShell.jsx";

const QUESTION_TYPES = [
  { k: "mcq", label: "Single choice", help: "One correct answer" },
  { k: "multi", label: "Multiple choice", help: "More than one correct answer" },
  { k: "truefalse", label: "True / False", help: "Boolean" },
  { k: "short", label: "Short answer", help: "Claude grades against rubric" },
  { k: "long", label: "Long answer", help: "Essay — Claude grades against rubric" },
  { k: "scenario", label: "Scenario", help: "Case-based reasoning" },
  { k: "number", label: "Number", help: "Exact numeric answer" },
];

const emptyQuestion = () => ({
  question_id: `qq_${Math.random().toString(36).slice(2, 10)}`,
  kind: "mcq", prompt: "", options: ["", ""], correct: [], rubric: "",
  points: 1, negative_points: 0, explanation: "", approved: false, ai_drafted: false,
});

export default function Modules() {
  const [modules, setModules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("basics");
  const [search, setSearch] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [pickedModules, setPickedModules] = useState(new Set());
  const [pickedEmployees, setPickedEmployees] = useState(new Set());

  const load = async () => {
    const [m, e] = await Promise.all([api.get("/training/modules"), api.get("/employees").catch(() => ({ data: [] }))]);
    setModules(m.data); setEmployees(e.data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => modules.filter((m) => !search || m.title.toLowerCase().includes(search.toLowerCase())),
    [modules, search],
  );

  const createNew = async () => {
    const r = await api.post("/training/modules", {
      title: "Untitled module", sop_markdown: "# SOP\n\nContent…", blocks: [],
    });
    openEditor(r.data); load();
  };

  const openEditor = async (m) => {
    const r = await api.get(`/training/modules/${m.module_id}`);
    setEditing({ ...r.data, _sop: r.data.versions?.[r.data.versions.length - 1]?.sop_markdown || "" });
    setTab("basics");
  };

  const save = async () => {
    await api.put(`/training/modules/${editing.module_id}`, {
      title: editing.title, owner_email: editing.owner_email, tags: editing.tags || [],
      estimated_minutes: editing.estimated_minutes, required: editing.required,
      prerequisites: editing.prerequisites || [], pass_mark_pct: editing.pass_mark_pct,
      max_attempts: editing.max_attempts, cooldown_hours: editing.cooldown_hours,
      resources: editing.resources || [], blocks: editing.blocks || [],
      sop_markdown: editing._sop || "", is_posh: false,
      questions: editing.questions || [],
    });
    toast.success("Saved"); load();
  };

  const draftAI = async () => {
    await save();
    const r = await api.post(`/training/modules/${editing.module_id}/draft-quiz`);
    toast.success(`${r.data.drafted} draft questions added — review and approve`);
    const upd = await api.get(`/training/modules/${editing.module_id}`); setEditing({ ...upd.data, _sop: editing._sop });
  };

  const addQuestion = () => setEditing({ ...editing, questions: [...(editing.questions || []), emptyQuestion()] });

  const updateQ = (qid, patch) => {
    setEditing({ ...editing, questions: (editing.questions || []).map((q) => q.question_id === qid ? { ...q, ...patch } : q) });
  };

  const removeQ = (qid) => setEditing({ ...editing, questions: (editing.questions || []).filter((q) => q.question_id !== qid) });

  const moveQ = (qid, dir) => {
    const qs = [...(editing.questions || [])];
    const i = qs.findIndex((q) => q.question_id === qid); const j = i + dir;
    if (j < 0 || j >= qs.length) return;
    [qs[i], qs[j]] = [qs[j], qs[i]];
    setEditing({ ...editing, questions: qs });
  };

  const multiAssign = async () => {
    if (pickedEmployees.size === 0 || pickedModules.size === 0) return toast.warning("Pick at least 1 employee + 1 module");
    const r = await api.post("/training/multi-assign", { employee_ids: [...pickedEmployees], module_ids: [...pickedModules] });
    toast.success(`${r.data.created} new assignments · ${r.data.skipped} already existed`);
    setAssignOpen(false); setPickedEmployees(new Set()); setPickedModules(new Set());
  };

  return (
    <div>
      <PageHeader
        eyebrow="TRAINING · MODULES"
        title="Modules"
        subtitle={`${filtered.length} of ${modules.length}`}
        stats={[
          { label: "Modules", value: modules.length },
          { label: "Required", value: modules.filter((m) => m.required).length },
          { label: "Avg Qs/module", value: (modules.reduce((a, m) => a + (m.questions?.length || 0), 0) / Math.max(1, modules.length)).toFixed(1) },
        ]}
        action={
          <div className="flex gap-2">
            <button onClick={() => setAssignOpen(true)} className="tt-btn-secondary flex items-center gap-2" data-testid="assign-btn"><Users size={14} /> Assign</button>
            <button onClick={createNew} className="tt-btn-primary flex items-center gap-2" data-testid="new-module-btn"><Plus size={14} /> New module</button>
          </div>
        }
      />

      <div className="flex gap-2 mb-4">
        <input className="tt-input !w-72" placeholder="Search modules…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="modules-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((m) => {
          const approved = (m.questions || []).filter((q) => q.approved).length;
          return (
            <button key={m.module_id} onClick={() => openEditor(m)} data-testid={`module-card-${m.module_id}`}
              className="tt-card p-5 text-left hover:border-[#5B9BFF] transition-colors group">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-medium text-base">{m.title}</div>
                {m.required && <span className="tt-badge-info shrink-0">required</span>}
              </div>
              <div className="font-mono text-[10px] tracking-widest text-[#71717A] uppercase mb-3">
                v{Math.max(...m.versions.map((v) => v.version))} · {m.estimated_minutes}m · pass {m.pass_mark_pct}%
              </div>
              <div className="flex gap-3 text-xs text-[#A1A1AA]">
                <span className="flex items-center gap-1"><FileText size={11} /> {(m.blocks || []).length} blocks</span>
                <span className="flex items-center gap-1"><HelpCircle size={11} /> {approved}/{(m.questions || []).length} questions</span>
              </div>
              {(m.tags || []).length > 0 && (
                <div className="flex gap-1 mt-3 flex-wrap">
                  {m.tags.slice(0, 4).map((t) => <span key={t} className="tt-badge-neutral text-[10px]">{t}</span>)}
                </div>
              )}
            </button>
          );
        })}
        {filtered.length === 0 && <div className="tt-card p-8 text-center text-sm text-[#71717A] md:col-span-2 xl:col-span-3">No modules yet. Click <span className="text-[#F4F4F5]">New module</span> to start.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.9)] z-40 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="min-h-screen flex items-start justify-center p-6">
            <div className="w-full max-w-4xl tt-card my-6" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 pt-5 pb-4 border-b border-[#1E1E1E] flex items-center gap-3">
                <input className="tt-input !border-0 !bg-transparent !px-0 text-xl font-semibold flex-1" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="module-title-input" />
                <button onClick={() => setEditing(null)} className="tt-btn-ghost text-[#71717A]"><X size={16} /></button>
              </div>

              <div className="px-6 pt-4 flex gap-1 border-b border-[#1E1E1E] -mb-px">
                {[
                  { k: "basics", label: "Basics" },
                  { k: "content", label: "Content & SOP" },
                  { k: "quiz", label: `Quiz (${(editing.questions || []).length})` },
                  { k: "preview", label: "Preview", icon: Eye },
                ].map((t) => (
                  <button key={t.k} onClick={() => setTab(t.k)} data-testid={`module-tab-${t.k}`}
                    className={`px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${tab === t.k ? "border-[#F4F4F5] text-[#F4F4F5]" : "border-transparent text-[#71717A] hover:text-[#F4F4F5]"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {tab === "basics" && (
                  <div className="space-y-5 max-w-xl">
                    <Field label="Owner email">
                      <input className="tt-input" value={editing.owner_email || ""} onChange={(e) => setEditing({ ...editing, owner_email: e.target.value })} />
                    </Field>
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="Estimated time (min)"><input type="number" className="tt-input font-mono" value={editing.estimated_minutes || 30} onChange={(e) => setEditing({ ...editing, estimated_minutes: parseInt(e.target.value || "0") })} /></Field>
                      <Field label="Pass mark (%)"><input type="number" className="tt-input font-mono" value={editing.pass_mark_pct || 70} onChange={(e) => setEditing({ ...editing, pass_mark_pct: parseFloat(e.target.value || "0") })} /></Field>
                      <Field label="Max attempts"><input type="number" className="tt-input font-mono" value={editing.max_attempts || 3} onChange={(e) => setEditing({ ...editing, max_attempts: parseInt(e.target.value || "0") })} /></Field>
                    </div>
                    <Field label="Tags" hint="Comma-separated (e.g. onboarding, design, posh)">
                      <input className="tt-input" value={(editing.tags || []).join(", ")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
                    </Field>
                    <label className="flex items-center gap-3 text-sm">
                      <input type="checkbox" checked={!!editing.required} onChange={(e) => setEditing({ ...editing, required: e.target.checked })} className="accent-[#5B9BFF] h-4 w-4" />
                      <span>Required module (counts toward readiness)</span>
                    </label>
                  </div>
                )}

                {tab === "content" && (
                  <div>
                    <div className="tt-label mb-2">SOP (Markdown)</div>
                    <textarea rows={14} className="tt-input font-mono text-sm leading-relaxed" value={editing._sop || ""} onChange={(e) => setEditing({ ...editing, _sop: e.target.value })} data-testid="sop-markdown" />
                    <div className="text-xs text-[#71717A] mt-2">Supports <code className="font-mono">#</code> headings, <code className="font-mono">**bold**</code>, <code className="font-mono">- lists</code>, <code className="font-mono">[link](url)</code>.</div>
                  </div>
                )}

                {tab === "quiz" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="font-medium">Quiz questions</div>
                        <div className="text-xs text-[#71717A] mt-0.5">{(editing.questions || []).filter((q) => q.approved).length} approved · trainee only sees approved</div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={draftAI} className="tt-btn-secondary text-xs flex items-center gap-1" data-testid="ai-draft-btn"><Zap size={12} /> Draft 5 with Claude</button>
                        <button onClick={addQuestion} className="tt-btn-primary text-xs flex items-center gap-1" data-testid="add-question-btn"><Plus size={12} /> Add question</button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {(editing.questions || []).map((q, i) => (
                        <QuestionCard key={q.question_id} q={q} index={i} total={(editing.questions || []).length}
                          onChange={(patch) => updateQ(q.question_id, patch)}
                          onRemove={() => removeQ(q.question_id)}
                          onMove={(dir) => moveQ(q.question_id, dir)} />
                      ))}
                      {(editing.questions || []).length === 0 && (
                        <div className="text-center py-10 text-sm text-[#71717A] border border-dashed border-[#262626] rounded-md">
                          No questions yet. Click <span className="text-[#F4F4F5]">Add question</span> or <span className="text-[#F4F4F5]">Draft 5 with Claude</span>.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {tab === "preview" && (
                  <div className="max-w-prose">
                    <div className="tt-label mb-3">Trainee view</div>
                    <h2 className="text-xl font-semibold mb-1">{editing.title}</h2>
                    <div className="text-xs font-mono text-[#71717A] mb-4">{editing.estimated_minutes}m · pass {editing.pass_mark_pct}%</div>
                    <pre className="font-mono text-xs whitespace-pre-wrap bg-[#0F0F0F] p-4 rounded border border-[#1E1E1E] mb-5">{editing._sop || "(SOP empty)"}</pre>
                    <div className="tt-label mb-2">Quiz · {(editing.questions || []).filter((q) => q.approved).length} questions</div>
                    {(editing.questions || []).filter((q) => q.approved).map((q, i) => (
                      <div key={q.question_id} className="tt-card p-4 mb-3">
                        <div className="text-xs text-[#71717A] font-mono mb-1">Q{i + 1} · {q.kind} · {q.points}pt</div>
                        <div className="text-sm mb-2">{q.prompt}</div>
                        {(q.kind === "mcq" || q.kind === "truefalse") && (q.kind === "truefalse" ? ["True", "False"] : q.options).map((o) => <div key={o} className="text-xs text-[#A1A1AA] py-0.5">○ {o}</div>)}
                        {q.kind === "multi" && q.options.map((o) => <div key={o} className="text-xs text-[#A1A1AA] py-0.5">☐ {o}</div>)}
                        {(q.kind === "short" || q.kind === "long" || q.kind === "scenario") && <div className="text-xs text-[#71717A] italic">(open answer)</div>}
                        {q.kind === "number" && <div className="text-xs text-[#71717A] italic">(numeric answer)</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-[#1E1E1E] flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="tt-btn-secondary text-sm">Close</button>
                <button onClick={save} className="tt-btn-primary text-sm" data-testid="save-module-btn">Save module</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {assignOpen && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.9)] z-40 flex items-center justify-center p-4" onClick={() => setAssignOpen(false)}>
          <div className="w-full max-w-3xl tt-card" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[#1E1E1E] flex justify-between items-center">
              <h2 className="text-lg font-semibold">Assign modules</h2>
              <button onClick={() => setAssignOpen(false)} className="tt-btn-ghost text-[#71717A]"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-0 border-b border-[#1E1E1E] max-h-[480px]">
              <div className="p-4 border-r border-[#1E1E1E] overflow-y-auto">
                <div className="tt-label mb-3">Employees · {pickedEmployees.size} picked</div>
                {employees.map((e) => (
                  <label key={e.employee_id} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer hover:bg-[#121212] px-2 rounded">
                    <input type="checkbox" className="accent-[#5B9BFF]"
                      checked={pickedEmployees.has(e.employee_id)}
                      onChange={() => { const n = new Set(pickedEmployees); n.has(e.employee_id) ? n.delete(e.employee_id) : n.add(e.employee_id); setPickedEmployees(n); }}
                      data-testid={`assign-emp-${e.employee_id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{e.display_name || e.legal_name || e.email}</div>
                      <div className="text-xs text-[#71717A] font-mono truncate">{e.role_title} · {e.team || "no team"}</div>
                    </div>
                  </label>
                ))}
                {employees.length === 0 && <div className="text-sm text-[#71717A] text-center py-6">No employees yet.</div>}
              </div>
              <div className="p-4 overflow-y-auto">
                <div className="tt-label mb-3">Modules · {pickedModules.size} picked</div>
                {modules.map((m) => (
                  <label key={m.module_id} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer hover:bg-[#121212] px-2 rounded">
                    <input type="checkbox" className="accent-[#5B9BFF]"
                      checked={pickedModules.has(m.module_id)}
                      onChange={() => { const n = new Set(pickedModules); n.has(m.module_id) ? n.delete(m.module_id) : n.add(m.module_id); setPickedModules(n); }}
                      data-testid={`assign-mod-${m.module_id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{m.title}</div>
                      <div className="text-xs text-[#71717A] font-mono truncate">{m.estimated_minutes}m · {(m.questions || []).filter((q) => q.approved).length}q</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 flex justify-between items-center">
              <div className="text-sm text-[#A1A1AA]">Will create <span className="font-mono text-[#F4F4F5]">{pickedEmployees.size * pickedModules.size}</span> assignments (duplicates skipped)</div>
              <button onClick={multiAssign} className="tt-btn-primary text-sm" data-testid="assign-confirm">Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="tt-label mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-xs text-[#71717A] mt-1">{hint}</div>}
    </div>
  );
}

function QuestionCard({ q, index, total, onChange, onRemove, onMove }) {
  const type = QUESTION_TYPES.find((t) => t.k === q.kind) || QUESTION_TYPES[0];
  const addOption = () => onChange({ options: [...(q.options || []), ""] });
  const setOption = (i, v) => { const o = [...(q.options || [])]; o[i] = v; onChange({ options: o }); };
  const removeOption = (i) => onChange({ options: (q.options || []).filter((_, j) => j !== i), correct: (q.correct || []).filter((c) => c !== q.options[i]) });

  const isObjective = ["mcq", "multi", "truefalse"].includes(q.kind);
  const isOpen = ["short", "long", "scenario"].includes(q.kind);

  const toggleCorrect = (opt) => {
    if (q.kind === "mcq" || q.kind === "truefalse") onChange({ correct: [opt] });
    else {
      const next = (q.correct || []).includes(opt) ? q.correct.filter((c) => c !== opt) : [...(q.correct || []), opt];
      onChange({ correct: next });
    }
  };

  return (
    <div className="tt-card p-4 border-l-2 border-l-[#262626] hover:border-l-[#5B9BFF] transition-colors" data-testid={`question-card-${q.question_id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="flex flex-col items-center text-[#71717A] mt-1">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="disabled:opacity-30 hover:text-[#F4F4F5]"><ArrowUp size={12} /></button>
          <span className="font-mono text-[10px] py-0.5">Q{index + 1}</span>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="disabled:opacity-30 hover:text-[#F4F4F5]"><ArrowDown size={12} /></button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center mb-2">
            <select value={q.kind} onChange={(e) => onChange({ kind: e.target.value, options: ["mcq", "multi", "truefalse"].includes(e.target.value) ? (q.options?.length ? q.options : ["", ""]) : [], correct: [] })} className="tt-input !w-auto !py-1 text-xs font-medium" data-testid={`q-type-${q.question_id}`}>
              {QUESTION_TYPES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
            </select>
            <span className="text-xs text-[#71717A]">{type.help}</span>
            {q.ai_drafted && <span className="tt-badge-info text-[10px]">AI drafted</span>}
            <div className="flex-1" />
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={!!q.approved} onChange={(e) => onChange({ approved: e.target.checked })} className="accent-[#22C55E] h-3.5 w-3.5" data-testid={`q-approve-${q.question_id}`} />
              <span className={q.approved ? "text-[#22C55E]" : "text-[#71717A]"}>Approved</span>
            </label>
          </div>
          <textarea rows={2} className="tt-input text-sm mb-3" placeholder="Question prompt" value={q.prompt} onChange={(e) => onChange({ prompt: e.target.value })} data-testid={`q-prompt-${q.question_id}`} />

          {isObjective && q.kind !== "truefalse" && (
            <div className="space-y-2 mb-3">
              <div className="tt-label">Options · tap the check to mark correct</div>
              {(q.options || []).map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button onClick={() => toggleCorrect(o)} className={`h-7 w-7 rounded border grid place-items-center shrink-0 transition-colors ${(q.correct || []).includes(o) ? "bg-[#22C55E]/15 border-[#22C55E] text-[#22C55E]" : "border-[#262626] text-[#71717A] hover:border-[#3F3F46]"}`} title="Mark correct">
                    <Check size={13} />
                  </button>
                  <input className="tt-input text-sm flex-1" value={o} onChange={(e) => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`} />
                  <button onClick={() => removeOption(i)} className="tt-btn-ghost text-[#71717A] hover:text-[#EF4444] p-1"><Trash2 size={12} /></button>
                </div>
              ))}
              <button onClick={addOption} className="tt-btn-ghost text-xs text-[#A1A1AA] hover:text-[#F4F4F5] flex items-center gap-1"><Plus size={11} /> Add option</button>
            </div>
          )}

          {q.kind === "truefalse" && (
            <div className="flex gap-2 mb-3">
              {["True", "False"].map((o) => (
                <button key={o} onClick={() => toggleCorrect(o)} className={`flex-1 py-2 rounded border text-sm transition-colors ${(q.correct || []).includes(o) ? "bg-[#22C55E]/15 border-[#22C55E] text-[#22C55E]" : "border-[#262626] text-[#A1A1AA] hover:border-[#3F3F46]"}`}>
                  {(q.correct || []).includes(o) && <Check size={13} className="inline mr-1" />}{o}
                </button>
              ))}
            </div>
          )}

          {q.kind === "number" && (
            <input className="tt-input text-sm mb-3 font-mono w-40" placeholder="Correct number" value={(q.correct || [])[0] || ""} onChange={(e) => onChange({ correct: [e.target.value] })} />
          )}

          {isOpen && (
            <textarea rows={2} className="tt-input text-xs mb-3 font-mono" placeholder="Grading rubric for Claude · what counts as a good answer" value={q.rubric || ""} onChange={(e) => onChange({ rubric: e.target.value })} />
          )}

          <div className="grid grid-cols-3 gap-2 mb-2">
            <div>
              <div className="tt-label mb-1">Points · right</div>
              <input type="number" step="0.5" className="tt-input !py-1 font-mono text-sm" value={q.points} onChange={(e) => onChange({ points: parseFloat(e.target.value || "0") })} data-testid={`q-points-${q.question_id}`} />
            </div>
            <div>
              <div className="tt-label mb-1">Points · wrong</div>
              <input type="number" step="0.5" className="tt-input !py-1 font-mono text-sm" value={q.negative_points || 0} onChange={(e) => onChange({ negative_points: parseFloat(e.target.value || "0") })} data-testid={`q-neg-${q.question_id}`} />
            </div>
            <div className="text-xs text-[#71717A] pt-5">0 = no negative marking</div>
          </div>
          <input className="tt-input text-xs" placeholder="Explanation shown after submit (optional)" value={q.explanation || ""} onChange={(e) => onChange({ explanation: e.target.value })} />
        </div>
        <button onClick={onRemove} className="tt-btn-ghost text-[#71717A] hover:text-[#EF4444]"><Trash2 size={13} /></button>
      </div>
    </div>
  );
}
